import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Chip,
  CircularProgress,
  ImageList,
  ImageListItem,
  Dialog,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Business,
  CalendarToday,
  LocationOn,
  ArrowBack,
  Share,
  Event,
  Directions,
  Close,
  NavigateNext,
  NavigateBefore,
  Schedule,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.paceestate.com';

const PublicSaleDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [sale, setSale] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [saleDays, setSaleDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);

  useEffect(() => {
    fetchSaleDetails();
    fetchPhotos();
  }, [id]);

  const fetchSaleDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/sales/${id}`);
      setSale(response.data);
      setSaleDays(response.data.sale_days || []);
    } catch (error) {
      console.error('Error fetching sale details:', error);
      if (error.response?.status === 404) {
        navigate('/public/sales');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/sales/${id}/photos`);
      setPhotos(response.data.photos || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
  };

  const formatSaleDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: sale?.name,
        text: `Check out this estate sale: ${sale?.name}`,
        url: url,
      }).catch(err => console.log('Error sharing:', err));
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  const handleAddToCalendar = () => {
    if (!sale) return;

    const startDate = new Date(sale.start_date).toISOString().replace(/-|:|\.\d+/g, '');
    const endDate = sale.end_date
      ? new Date(sale.end_date).toISOString().replace(/-|:|\.\d+/g, '')
      : startDate;

    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(sale.name)}&dates=${startDate}/${endDate}&details=${encodeURIComponent(sale.description || '')}&location=${encodeURIComponent(`${sale.address}, ${sale.city}, ${sale.state} ${sale.zip_code}`)}`;

    window.open(calendarUrl, '_blank');
  };

  const handleGetDirections = () => {
    if (!sale) return;
    const address = `${sale.address}, ${sale.city}, ${sale.state} ${sale.zip_code}`;
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    window.open(mapsUrl, '_blank');
  };

  const openPhotoGallery = (index) => {
    setPhotoIndex(index);
    setSelectedPhoto(photos[index]);
  };

  const handleNextPhoto = () => {
    const nextIndex = (photoIndex + 1) % photos.length;
    setPhotoIndex(nextIndex);
    setSelectedPhoto(photos[nextIndex]);
  };

  const handlePrevPhoto = () => {
    const prevIndex = (photoIndex - 1 + photos.length) % photos.length;
    setPhotoIndex(prevIndex);
    setSelectedPhoto(photos[prevIndex]);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!sale) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h5" color="text.secondary">
          Sale not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Business sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, cursor: 'pointer' }} onClick={() => navigate('/')}>
            PaceEstate
          </Typography>
          <Button color="inherit" onClick={() => navigate('/')}>
            Home
          </Button>
          <Button color="inherit" onClick={() => navigate('/public/sales')}>
            Sales
          </Button>
          <Button color="inherit" onClick={() => navigate('/login')} sx={{ ml: 2 }}>
            Staff Login
          </Button>
        </Toolbar>
      </AppBar>

      {/* Back Button */}
      <Container sx={{ mt: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/public/sales')}
          sx={{ mb: 2 }}
        >
          Back to All Sales
        </Button>
      </Container>

      {/* Sale Header */}
      <Container sx={{ mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              {sale.name}
            </Typography>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Chip label={sale.status} color="primary" />
            </Box>
          </Grid>
          <Grid item xs={12} md={4} textAlign={{ xs: 'left', md: 'right' }}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'column' }} gap={1}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Event />}
                onClick={handleAddToCalendar}
                fullWidth
              >
                Add to Calendar
              </Button>
              <Button
                variant="outlined"
                startIcon={<Directions />}
                onClick={handleGetDirections}
                fullWidth
              >
                Get Directions
              </Button>
              <Button
                variant="outlined"
                startIcon={<Share />}
                onClick={handleShare}
                fullWidth
              >
                Share
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Container sx={{ mb: 6 }}>
        <Grid container spacing={4}>
          {/* Photo Gallery */}
          <Grid item xs={12} md={8}>
            {photos.length > 0 ? (
              <Card>
                <ImageList variant="quilted" cols={3} gap={8}>
                  {photos.map((photo, index) => (
                    <ImageListItem
                      key={photo.id}
                      cols={index === 0 ? 2 : 1}
                      rows={index === 0 ? 2 : 1}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openPhotoGallery(index)}
                    >
                      <img
                        src={`${API_URL}${photo.file_path}`}
                        alt={photo.caption || sale.name}
                        loading="lazy"
                        style={{ borderRadius: 8 }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              </Card>
            ) : (
              <Card sx={{ p: 4, textAlign: 'center' }}>
                <Business sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No photos available yet
                </Typography>
              </Card>
            )}

            {/* Description */}
            <Card sx={{ mt: 3, p: 3 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                About This Sale
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {sale.description || 'Details coming soon...'}
              </Typography>

              {sale.access_instructions && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Access Instructions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sale.access_instructions}
                  </Typography>
                </>
              )}
            </Card>
          </Grid>

          {/* Sale Details Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Date & Time */}
            <Card sx={{ mb: 2, p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <CalendarToday color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Sale Dates
                </Typography>
              </Box>

              {saleDays.length > 0 ? (
                <Box>
                  {saleDays.map((day, index) => (
                    <Box key={index} mb={2}>
                      <Typography variant="body1" fontWeight="bold">
                        {formatSaleDate(day.sale_date)}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <Schedule fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {formatTime(day.start_time)} - {formatTime(day.end_time)}
                        </Typography>
                      </Box>
                      {day.discount_percentage > 0 && (
                        <Chip
                          label={`${day.discount_percentage}% OFF`}
                          color="secondary"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                      {day.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                          {day.notes}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <>
                  <Typography variant="body1" fontWeight="bold">
                    {formatSaleDate(sale.start_date)}
                  </Typography>
                  {sale.end_date && sale.end_date !== sale.start_date && (
                    <Typography variant="body2" color="text.secondary">
                      through {formatSaleDate(sale.end_date)}
                    </Typography>
                  )}
                </>
              )}
            </Card>

            {/* Location */}
            <Card sx={{ mb: 2, p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <LocationOn color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Location
                </Typography>
              </Box>
              <Typography variant="body1" gutterBottom>
                {sale.address}
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {sale.city}, {sale.state} {sale.zip_code}
              </Typography>
              {sale.county && (
                <Typography variant="body2" color="text.secondary">
                  {sale.county} County
                </Typography>
              )}
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Directions />}
                onClick={handleGetDirections}
                sx={{ mt: 2 }}
              >
                Get Directions
              </Button>
            </Card>

            {/* Contact */}
            <Card sx={{ p: 3, backgroundColor: 'primary.main', color: 'white' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Questions?
              </Typography>
              <Typography variant="body2" paragraph sx={{ opacity: 0.9 }}>
                Contact PaceEstate for more information about this sale.
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                onClick={() => navigate('/#contact')}
              >
                Contact Us
              </Button>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Photo Gallery Modal */}
      <Dialog
        open={!!selectedPhoto}
        onClose={() => setSelectedPhoto(null)}
        maxWidth="lg"
        fullWidth
      >
        <Box sx={{ position: 'relative', bgcolor: 'black' }}>
          <IconButton
            onClick={() => setSelectedPhoto(null)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              zIndex: 1,
            }}
          >
            <Close />
          </IconButton>

          {photos.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevPhoto}
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <NavigateBefore />
              </IconButton>

              <IconButton
                onClick={handleNextPhoto}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <NavigateNext />
              </IconButton>
            </>
          )}

          {selectedPhoto && (
            <img
              src={`${API_URL}${selectedPhoto.file_path}`}
              alt={selectedPhoto.caption || sale.name}
              style={{ width: '100%', height: 'auto', maxHeight: '90vh', objectFit: 'contain' }}
            />
          )}

          {selectedPhoto?.caption && (
            <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.7)', color: 'white' }}>
              <Typography variant="body1">{selectedPhoto.caption}</Typography>
            </Box>
          )}

          <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.7)', color: 'white', textAlign: 'center' }}>
            <Typography variant="body2">
              Photo {photoIndex + 1} of {photos.length}
            </Typography>
          </Box>
        </Box>
      </Dialog>

      {/* Footer */}
      <Box sx={{ py: 4, backgroundColor: 'primary.main', color: 'white' }}>
        <Container>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                © {new Date().getFullYear()} PaceEstate. All rights reserved.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} textAlign={{ xs: 'left', md: 'right' }}>
              <Button color="inherit" onClick={() => navigate('/')}>
                Home
              </Button>
              <Button color="inherit" onClick={() => navigate('/public/sales')}>
                All Sales
              </Button>
              <Button color="inherit" onClick={() => navigate('/login')}>
                Staff Login
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicSaleDetail;
